import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetFooter,
    SheetHeader,
    SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import {
    ColumnDef,
    ColumnFiltersState,
    PaginationState,
    SortingState,
    flexRender,
    getCoreRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { format } from 'date-fns';
import { ArrowDown, ArrowUp, ArrowUpDown, Loader2, MoreHorizontal, Plus, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import { trpc } from '~/api/trpc';
import { useEquipmentTableFilteringStore } from '~/stores/equipment-table-filtering-store';
import Content from '../components/layout/Content';
import { toast } from 'sonner';
import { equipment_status } from '@server/prisma-generated/enums';

type Equipment = {
    id: number;
    name: string;
    dateOfPurchase: string;
    serialNumber: string | null;
    status: equipment_status;
    categoryId: number | null;
    storeId: number | null;
    equipment_category: { id: number; name: string } | null;
    store: { id: number; name: string } | null;
    createdAt: string;
    updatedAt: string;
    deletedAt: string | null;
};

const statusLabels: Record<equipment_status, string> = {
    AVAILABLE: 'Verfügbar',
    DEFECT: 'Defekt',
};

const statusColors: Record<equipment_status, string> = {
    AVAILABLE: 'bg-green-100 text-green-800',
    DEFECT: 'bg-red-100 text-red-800',
};

const equipmentColumns: ColumnDef<Equipment>[] = [
    {
        accessorKey: 'id',
        header: 'ID',
    },
    {
        accessorKey: 'name',
        header: ({ column }) => {
            return (
                <Button
                    variant='ghost'
                    onClick={() => {
                        const currentSort = column.getIsSorted();
                        if (currentSort === 'asc') {
                            column.toggleSorting(true);
                        } else if (currentSort === 'desc') {
                            column.clearSorting();
                        } else {
                            column.toggleSorting(false);
                        }
                    }}
                >
                    Name
                    {column.getIsSorted() === 'asc' ? (
                        <ArrowUp className='ml-2 h-4 w-4' />
                    ) : column.getIsSorted() === 'desc' ? (
                        <ArrowDown className='ml-2 h-4 w-4' />
                    ) : (
                        <ArrowUpDown className='ml-2 h-4 w-4 opacity-50' />
                    )}
                </Button>
            );
        },
        enableSorting: true,
    },
    {
        id: 'dateOfPurchase',
        header: 'Anschaffungsdatum',
        cell: ({ row }) => {
            const dateStr = row.original.dateOfPurchase;
            if (!dateStr) return <div>-</div>;
            const date = new Date(dateStr);
            return <div>{format(date, 'dd.MM.yyyy')}</div>;
        },
    },
    {
        accessorKey: 'serialNumber',
        header: 'Seriennummer',
        cell: ({ row }) => {
            return <div>{row.original.serialNumber || '-'}</div>;
        },
    },
    {
        id: 'category',
        header: 'Kategorie',
        cell: ({ row }) => {
            return <div>{row.original.equipment_category?.name ?? '-'}</div>;
        },
    },
    {
        id: 'status',
        header: 'Status',
        cell: ({ row }) => {
            const status = row.original.status;
            return (
                <Badge variant='outline' className={statusColors[status]}>
                    {statusLabels[status]}
                </Badge>
            );
        },
    },
    {
        id: 'store',
        header: 'Filiale',
        cell: ({ row }) => {
            return <div>{row.original.store?.name ?? '-'}</div>;
        },
    },
];

interface EquipmentFormData {
    name: string;
    dateOfPurchase: string;
    serialNumber: string;
    categoryId: number | null;
    status: equipment_status;
    storeId: number | null;
}

interface EquipmentSheetProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    equipment: Equipment | null;
    onSave: (data: EquipmentFormData) => void;
    onDelete: () => void;
    isSaving: boolean;
}

const EquipmentSheet = ({ open, onOpenChange, equipment, onSave, onDelete, isSaving }: EquipmentSheetProps) => {
    const [name, setName] = useState('');
    const [dateOfPurchase, setDateOfPurchase] = useState('');
    const [serialNumber, setSerialNumber] = useState('');
    const [categoryId, setCategoryId] = useState<number | null>(null);
    const [status, setStatus] = useState<equipment_status>(equipment_status.AVAILABLE);
    const [storeId, setStoreId] = useState<number | null>(null);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);

    const { data: categories } = trpc.equipmentCategories.getAllSimple.useQuery();
    const { data: stores } = trpc.stores.getAllSimple.useQuery();

    useEffect(() => {
        if (equipment) {
            setName(equipment.name);
            setDateOfPurchase(equipment.dateOfPurchase ? format(new Date(equipment.dateOfPurchase), 'yyyy-MM-dd') : '');
            setSerialNumber(equipment.serialNumber || '');
            setCategoryId(equipment.categoryId);
            setStatus(equipment.status);
            setStoreId(equipment.storeId);
        } else {
            setName('');
            setDateOfPurchase('');
            setSerialNumber('');
            setCategoryId(null);
            setStatus(equipment_status.AVAILABLE);
            setStoreId(null);
        }
    }, [equipment, open]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (name.trim() && categoryId && storeId) {
            onSave({
                name: name.trim(),
                dateOfPurchase,
                serialNumber,
                categoryId: categoryId,
                status,
                storeId,
            });
        }
    };

    return (
        <>
            <Sheet open={open} onOpenChange={onOpenChange}>
                <SheetContent className='overflow-y-auto'>
                    <form onSubmit={handleSubmit}>
                        <SheetHeader>
                            <SheetTitle>
                                {equipment ? 'Gerät bearbeiten' : 'Neues Gerät'}
                            </SheetTitle>
                            <SheetDescription>
                                {equipment
                                    ? 'Bearbeiten Sie die Daten des Geräts.'
                                    : 'Geben Sie die Daten für das neue Gerät ein.'}
                            </SheetDescription>
                        </SheetHeader>
                        <div className='flex flex-col gap-4 py-4 px-4'>
                            <div className='flex flex-col gap-2'>
                                <Label htmlFor='name'>Name</Label>
                                <Input
                                    id='name'
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder='Gerätename eingeben'
                                    autoFocus
                                />
                            </div>
                            <div className='flex flex-col gap-2'>
                                <Label htmlFor='dateOfPurchase'>Datum der Anschaffung</Label>
                                <Input
                                    id='dateOfPurchase'
                                    type='date'
                                    value={dateOfPurchase}
                                    onChange={(e) => setDateOfPurchase(e.target.value)}
                                />
                            </div>
                            <div className='flex flex-col gap-2'>
                                <Label htmlFor='serialNumber'>Seriennummer</Label>
                                <Input
                                    id='serialNumber'
                                    value={serialNumber}
                                    onChange={(e) => setSerialNumber(e.target.value)}
                                    placeholder='Seriennummer eingeben (optional)'
                                />
                            </div>
                            <div className='flex flex-col gap-2'>
                                <Label htmlFor='status'>Status</Label>
                                <Select
                                    value={status}
                                    onValueChange={(value) => setStatus(value as equipment_status)}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder='Status wählen' />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value={equipment_status.AVAILABLE}>Verfügbar</SelectItem>
                                        <SelectItem value={equipment_status.DEFECT}>Defekt</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className='flex flex-col gap-2'>
                                <Label htmlFor='category'>Gerätekategorie</Label>
                                <Select
                                    value={categoryId?.toString() ?? ''}
                                    onValueChange={(value) => setCategoryId(parseInt(value, 10))}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder='Kategorie wählen' />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {categories?.map((cat) => (
                                            <SelectItem key={cat.id} value={cat.id.toString()}>
                                                {cat.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <p className='text-xs text-muted-foreground'>
                                    Die Gerätekategorie wird verwendet, um die Art des Geräts zu definieren.
                                </p>
                            </div>
                            <div className='flex flex-col gap-2'>
                                <Label htmlFor='store'>Filiale</Label>
                                <Select
                                    value={storeId?.toString() ?? ''}
                                    onValueChange={(value) => setStoreId(parseInt(value, 10))}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder='Filiale wählen' />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {stores?.map((store) => (
                                            <SelectItem key={store.id} value={store.id.toString()}>
                                                {store.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <p className='text-xs text-muted-foreground'>
                                    Die Filiale in der das Gerät sich befindet.
                                </p>
                            </div>
                        </div>
                        <SheetFooter>
                            {equipment && (
                                <Button
                                    type='button'
                                    variant='destructive'
                                    onClick={() => setShowDeleteDialog(true)}
                                    disabled={isSaving}
                                >
                                    <Trash2 className='h-4 w-4 mr-2' />
                                    Löschen
                                </Button>
                            )}
                            <Button type='submit' disabled={isSaving || !name.trim() || !categoryId || !storeId}>
                                {isSaving && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
                                {equipment ? 'Speichern' : 'Erstellen'}
                            </Button>
                        </SheetFooter>
                    </form>
                </SheetContent>
            </Sheet>

            <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Gerät löschen?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Möchten Sie das Gerät &quot;{equipment?.name}&quot; wirklich löschen? Diese Aktion
                            kann nicht rückgängig gemacht werden.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => {
                                setShowDeleteDialog(false);
                                onDelete();
                            }}
                            className='bg-destructive text-destructive-foreground hover:bg-destructive/90'
                        >
                            Löschen
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
};

const EquipmentDataTable = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const isInitialized = useRef<boolean>(false);

    const {
        sorting: storeSorting,
        columnFilters: storeColumnFilters,
        pagination: storePagination,
        setSorting,
        setColumnFilters,
        setPagination,
    } = useEquipmentTableFilteringStore(
        useShallow((state) => ({
            sorting: state.sorting,
            setSorting: state.setSorting,
            columnFilters: state.columnFilters,
            setColumnFilters: state.setColumnFilters,
            pagination: state.pagination,
            setPagination: state.setPagination,
        }))
    );

    const [sheetOpen, setSheetOpen] = useState(false);
    const [selectedEquipment, setSelectedEquipment] = useState<Equipment | null>(null);

    useEffect(() => {
        if (isInitialized.current) return;
        isInitialized.current = true;

        const newParams = new URLSearchParams(searchParams);
        let needsUrlUpdate = false;

        const urlSortField = searchParams.get('sort');
        const urlSortDir = searchParams.get('dir') || 'asc';

        if (urlSortField) {
            const urlSortingState: SortingState = [{ id: urlSortField, desc: urlSortDir === 'desc' }];
            if (JSON.stringify(urlSortingState) !== JSON.stringify(storeSorting)) {
                setSorting(urlSortingState);
            }
        } else if (storeSorting.length > 0) {
            newParams.set('sort', storeSorting[0].id);
            newParams.set('dir', storeSorting[0].desc ? 'desc' : 'asc');
            needsUrlUpdate = true;
        }

        const urlPage = searchParams.get('page');
        const urlSize = searchParams.get('size');

        if (urlPage !== null || urlSize !== null) {
            const pageIndex = urlPage !== null ? Math.max(0, parseInt(urlPage, 10) || 0) : storePagination.pageIndex;
            const pageSize = urlSize !== null ? Math.max(1, parseInt(urlSize, 10) || 10) : storePagination.pageSize;
            if (pageIndex !== storePagination.pageIndex || pageSize !== storePagination.pageSize) {
                setPagination({ pageIndex, pageSize });
            }
        } else {
            newParams.set('page', String(storePagination.pageIndex));
            newParams.set('size', String(storePagination.pageSize));
            needsUrlUpdate = true;
        }

        const urlFilters: ColumnFiltersState = [];
        for (const [key, value] of searchParams.entries()) {
            if (key.startsWith('filter_')) {
                urlFilters.push({ id: key.replace('filter_', ''), value });
            }
        }
        if (urlFilters.length > 0) {
            setColumnFilters(urlFilters);
        }

        if (needsUrlUpdate) {
            setSearchParams(newParams, { replace: true });
        }
    }, [searchParams, setSearchParams, storeSorting, storePagination, setSorting, setPagination, setColumnFilters]);

    const queryPage = parseInt(searchParams.get('page') || String(storePagination.pageIndex), 10);
    const queryPageSize = parseInt(searchParams.get('size') || String(storePagination.pageSize), 10);
    const queryFilters: Record<string, string | undefined> = {};
    for (const [key, value] of searchParams.entries()) {
        if (key.startsWith('filter_')) {
            queryFilters[key.replace('filter_', '')] = value;
        }
    }

    const utils = trpc.useUtils();

    const {
        data: equipmentResponse,
        isLoading,
        isFetching,
    } = trpc.equipment.getAll.useQuery(
        {
            page: queryPage,
            pageSize: queryPageSize,
            filters: queryFilters,
        },
        {
            placeholderData: (prev) => prev,
        }
    );

    const createMutation = trpc.equipment.create.useMutation({
        onSuccess: () => {
            utils.equipment.getAll.invalidate();
            toast.success('Gerät erfolgreich erstellt');
            setSheetOpen(false);
        },
        onError: (error) => {
            toast.error('Fehler beim Erstellen des Geräts', { description: error.message });
        },
    });

    const updateMutation = trpc.equipment.update.useMutation({
        onSuccess: () => {
            utils.equipment.getAll.invalidate();
            toast.success('Gerät erfolgreich aktualisiert');
            setSheetOpen(false);
        },
        onError: (error) => {
            toast.error('Fehler beim Aktualisieren des Geräts', { description: error.message });
        },
    });

    const deleteMutation = trpc.equipment.delete.useMutation({
        onSuccess: () => {
            utils.equipment.getAll.invalidate();
            toast.success('Gerät erfolgreich gelöscht');
            setSheetOpen(false);
        },
        onError: (error) => {
            toast.error('Fehler beim Löschen des Geräts', { description: error.message });
        },
    });

    useEffect(() => {
        if (!isInitialized.current) return;
        const newParams = new URLSearchParams(searchParams);
        let changed = false;

        if (storeSorting.length > 0) {
            if (searchParams.get('sort') !== storeSorting[0].id) {
                newParams.set('sort', storeSorting[0].id);
                changed = true;
            }
            const dir = storeSorting[0].desc ? 'desc' : 'asc';
            if (searchParams.get('dir') !== dir) {
                newParams.set('dir', dir);
                changed = true;
            }
        } else {
            if (searchParams.has('sort')) {
                newParams.delete('sort');
                newParams.delete('dir');
                changed = true;
            }
        }

        if (changed) {
            setSearchParams(newParams, { replace: true });
        }
    }, [storeSorting, searchParams, setSearchParams]);

    useEffect(() => {
        if (!isInitialized.current) return;
        const currentPage = searchParams.get('page');
        const currentSize = searchParams.get('size');
        const storePage = String(storePagination.pageIndex);
        const storeSize = String(storePagination.pageSize);

        if (currentPage !== storePage || currentSize !== storeSize) {
            const newParams = new URLSearchParams(searchParams);
            newParams.set('page', storePage);
            newParams.set('size', storeSize);
            setSearchParams(newParams, { replace: true });
        }
    }, [storePagination, searchParams, setSearchParams]);

    // Sync filter state with URL
    useEffect(() => {
        if (!isInitialized.current) return;
        const newParams = new URLSearchParams(searchParams);
        let filtersChanged = false;
        const urlFiltersMap = new Map<string, string>();
        Array.from(newParams.keys())
            .filter((key) => key.startsWith('filter_'))
            .forEach((key) => urlFiltersMap.set(key.replace('filter_', ''), newParams.get(key)!));
        const storeFiltersMap = new Map<string, string>();
        storeColumnFilters.forEach((f) => storeFiltersMap.set(f.id, String(f.value)));

        urlFiltersMap.forEach((_, id) => {
            if (!storeFiltersMap.has(id)) {
                newParams.delete(`filter_${id}`);
                filtersChanged = true;
            }
        });
        storeFiltersMap.forEach((value, id) => {
            if (urlFiltersMap.get(id) !== value) {
                if (value) {
                    newParams.set(`filter_${id}`, value);
                } else {
                    newParams.delete(`filter_${id}`);
                }
                filtersChanged = true;
            }
        });

        if (filtersChanged) {
            setSearchParams(newParams, { replace: true });
        }
    }, [storeColumnFilters, searchParams, setSearchParams]);

    const table = useReactTable({
        data: (equipmentResponse?.data ?? []) as Equipment[],
        columns: equipmentColumns,
        pageCount: equipmentResponse?.pageCount ?? -1,
        state: {
            sorting: storeSorting,
            columnFilters: storeColumnFilters,
            pagination: storePagination,
        },
        onPaginationChange: setPagination,
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        manualPagination: true,
        manualSorting: true,
        manualFiltering: true,
        getCoreRowModel: getCoreRowModel(),
    });

    const handleOpenCreate = () => {
        setSelectedEquipment(null);
        setSheetOpen(true);
    };

    const handleOpenEdit = (equipment: Equipment) => {
        setSelectedEquipment(equipment);
        setSheetOpen(true);
    };

    const handleSave = (data: EquipmentFormData) => {
        if (selectedEquipment) {
            updateMutation.mutate({
                id: selectedEquipment.id,
                name: data.name,
                dateOfPurchase: data.dateOfPurchase,
                serialNumber: data.serialNumber,
                equipmentCategoryId: data.categoryId!,
                status: data.status,
                storeId: data.storeId!,
            });
        } else {
            createMutation.mutate({
                name: data.name,
                dateOfPurchase: data.dateOfPurchase,
                serialNumber: data.serialNumber,
                equipmentCategoryId: data.categoryId!,
                status: data.status,
                storeId: data.storeId!,
            });
        }
    };

    const handleDelete = () => {
        if (selectedEquipment) {
            deleteMutation.mutate({ id: selectedEquipment.id });
        }
    };

    const isMutating = createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;

    if (isLoading && !equipmentResponse) {
        return (
            <div className='bg-white rounded-lg shadow-sm p-6'>
                <div className='flex items-center justify-between py-4'>
                    <Skeleton className='h-10 w-[250px]' />
                    <Skeleton className='h-10 w-[150px]' />
                </div>
                <div className='rounded-md border border-gray-200'>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                {equipmentColumns.map((_, index) => (
                                    <TableHead key={index}>
                                        <Skeleton className='h-6 w-full' />
                                    </TableHead>
                                ))}
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {Array.from({ length: 5 }).map((_, rowIndex) => (
                                <TableRow key={rowIndex}>
                                    {equipmentColumns.map((_, colIndex) => (
                                        <TableCell key={colIndex}>
                                            <Skeleton className='h-6 w-full' />
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            </div>
        );
    }

    return (
        <div className='bg-white rounded-lg shadow-sm p-6'>
            <div className='flex items-center justify-between py-4'>
                <div className='flex gap-4 items-center'>
                    <Input
                        placeholder='Nach Name filtern...'
                        value={(table.getColumn('name')?.getFilterValue() as string) ?? ''}
                        onChange={(event) => {
                            table.getColumn('name')?.setFilterValue(event.target.value);
                        }}
                        className='max-w-sm'
                    />
                    {isFetching && (
                        <div className='flex items-center text-sm text-muted-foreground'>
                            <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                            Laden...
                        </div>
                    )}
                </div>
                <Button onClick={handleOpenCreate}>
                    <Plus className='mr-2 h-4 w-4' />
                    Neues Gerät
                </Button>
            </div>

            <div className='rounded-md border border-gray-200 overflow-hidden'>
                <Table>
                    <TableHeader className='bg-gray-50'>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <TableHead key={header.id} className='font-medium text-gray-700'>
                                        {header.isPlaceholder
                                            ? null
                                            : flexRender(header.column.columnDef.header, header.getContext())}
                                    </TableHead>
                                ))}
                                <TableHead className='w-[50px]' />
                            </TableRow>
                        ))}
                    </TableHeader>
                    <TableBody>
                        {table.getRowModel().rows?.length ? (
                            table.getRowModel().rows.map((row) => (
                                <TableRow
                                    key={row.id}
                                    className='cursor-pointer hover:bg-gray-50 transition-colors'
                                    onClick={() => handleOpenEdit(row.original)}
                                >
                                    {row.getVisibleCells().map((cell) => (
                                        <TableCell key={cell.id}>
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </TableCell>
                                    ))}
                                    <TableCell>
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                                <Button variant='ghost' className='h-8 w-8 p-0'>
                                                    <span className='sr-only'>Menü öffnen</span>
                                                    <MoreHorizontal className='h-4 w-4' />
                                                </Button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align='end'>
                                                <DropdownMenuItem
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleOpenEdit(row.original);
                                                    }}
                                                >
                                                    Bearbeiten
                                                </DropdownMenuItem>
                                                <DropdownMenuSeparator />
                                                <DropdownMenuItem
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigator.clipboard.writeText(String(row.original.id));
                                                        toast.success('ID kopiert');
                                                    }}
                                                >
                                                    ID kopieren
                                                </DropdownMenuItem>
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell
                                    colSpan={equipmentColumns.length + 1}
                                    className='h-24 text-center'
                                >
                                    {isFetching ? (
                                        <div className='flex justify-center items-center'>
                                            <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                                            Daten werden geladen...
                                        </div>
                                    ) : (
                                        'Keine Geräte gefunden.'
                                    )}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>

            <div className='flex items-center justify-between space-x-2 py-4 mt-2'>
                <div className='flex items-center space-x-2'>
                    <select
                        value={table.getState().pagination.pageSize}
                        onChange={(e) => {
                            table.setPageSize(Number(e.target.value));
                        }}
                        className='border border-gray-300 rounded px-2 py-1 text-sm'
                        disabled={isFetching}
                    >
                        {[10, 20, 30, 40, 50].map((pageSize) => (
                            <option key={pageSize} value={pageSize}>
                                {pageSize} pro Seite
                            </option>
                        ))}
                    </select>
                    <div className='text-sm text-muted-foreground'>
                        Seite {table.getState().pagination.pageIndex + 1} von{' '}
                        {table.getPageCount() > 0 ? table.getPageCount() : 1}
                    </div>
                    <Button
                        variant='outline'
                        size='sm'
                        onClick={() => table.previousPage()}
                        disabled={!table.getCanPreviousPage() || isFetching}
                    >
                        &lt;
                    </Button>
                    <Button
                        variant='outline'
                        size='sm'
                        onClick={() => table.nextPage()}
                        disabled={!table.getCanNextPage() || isFetching}
                    >
                        &gt;
                    </Button>
                </div>
            </div>

            <EquipmentSheet
                open={sheetOpen}
                onOpenChange={setSheetOpen}
                equipment={selectedEquipment}
                onSave={handleSave}
                onDelete={handleDelete}
                isSaving={isMutating}
            />
        </div>
    );
};

const Equipments = () => {
    return (
        <Content>
            <div className='container mx-auto py-10'>
                <h1 className='text-2xl font-bold mb-6'>Geräte</h1>
                <EquipmentDataTable />
            </div>
        </Content>
    );
};

export default Equipments;
